import type {
  ManualFrameClockOptions,
  ManualFrameRequest,
  ManualFrameRequestOverrides,
} from './types'

export class ManualFrameClock {
  readonly fps: number
  readonly width: number
  readonly height: number
  readonly pixelRatio: number
  readonly startTimeSeconds: number

  constructor(options: ManualFrameClockOptions) {
    validatePositiveFinite(options.fps, 'fps')
    validatePositiveFinite(options.width, 'width')
    validatePositiveFinite(options.height, 'height')
    validatePositiveFinite(options.pixelRatio ?? 1, 'pixelRatio')
    if (options.startTimeSeconds !== undefined) {
      validateNonNegativeFinite(options.startTimeSeconds, 'startTimeSeconds')
    }

    this.fps = options.fps
    this.width = options.width
    this.height = options.height
    this.pixelRatio = options.pixelRatio ?? 1
    this.startTimeSeconds = options.startTimeSeconds ?? 0
  }

  frameAt(frameNumber: number, overrides: ManualFrameRequestOverrides = {}): ManualFrameRequest {
    validateFrameNumber(frameNumber)
    const timestampSeconds = this.startTimeSeconds + frameNumber / this.fps
    const deltaSeconds = frameNumber === 0 ? 0 : 1 / this.fps

    return {
      frameNumber,
      timestampSeconds,
      deltaSeconds,
      width: this.width,
      height: this.height,
      pixelRatio: this.pixelRatio,
      ...overrides,
    }
  }
}

export function createManualFrameClock(options: ManualFrameClockOptions): ManualFrameClock {
  return new ManualFrameClock(options)
}

function validateFrameNumber(value: number): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error('ManualFrameClock frameNumber must be a non-negative integer.')
  }
}

function validatePositiveFinite(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`ManualFrameClock ${name} must be a positive finite number.`)
  }
}

function validateNonNegativeFinite(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`ManualFrameClock ${name} must be a non-negative finite number.`)
  }
}
